import React, { useState, useCallback } from 'react';
import { 
  HeadphonesIcon, 
  User, 
  Phone, 
  Search,
  DollarSign,
  TrendingUp,
  PiggyBank,
  Wallet,
  Eye,
  EyeOff,
  Filter,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { CustomerInfo, CustomerBalance, CustomerInvestment, CustomerPurchase } from '../types/api';

const AtencionSection: React.FC = () => {
  const {
    getCustomerByPhone,
    getCustomerInfo,
    getCustomerBalance,
    getCustomerInvestments,
    getCustomerPurchases,
    getUserClabe
  } = useAdminAPI();

  const [activeTab, setActiveTab] = useState<'general' | 'inversiones' | 'saldo'>('general');
  const [searchPhone, setSearchPhone] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [showBalance, setShowBalance] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [balanceInfo, setBalanceInfo] = useState<CustomerBalance | null>(null);
  const [investments, setInvestments] = useState<CustomerInvestment[]>([]);
  const [purchases, setPurchases] = useState<CustomerPurchase[]>([]);
  const [userClabe, setUserClabe] = useState<string | null>(null);

  // Date ranges for filters
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const loadCustomerData = useCallback(async (customerId: string, phone?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Obtener CLABE con manejo de errores mejorado
      let clabeResponse: { clabe: string | null } = { clabe: null };
      try {
        if (phone) {
          console.log('📞 Obteniendo CLABE usando teléfono:', phone);
          clabeResponse = await getUserClabe(phone, true);
        } else {
          console.log('🆔 Obteniendo CLABE usando customerId:', customerId);
          clabeResponse = await getUserClabe(customerId);
        }
        console.log('✅ CLABE obtenida exitosamente del endpoint:', clabeResponse);
      } catch (clabeError) {
        console.warn('⚠️ Error al obtener CLABE del endpoint, intentando fallback:', clabeError);
        // Fallback: intentar con el otro método si uno falla
        try {
          if (phone) {
            // Si falló con teléfono, intentar con customerId
            console.log('🔄 Fallback: intentando obtener CLABE con customerId:', customerId);
            clabeResponse = await getUserClabe(customerId);
          } else if (customerId) {
            // Si falló con customerId y tenemos teléfono, intentar con teléfono
            // (aunque en este caso probablemente no tengamos teléfono aquí)
            console.log('🔄 Fallback: customerId falló, pero no hay teléfono disponible');
          }
        } catch (fallbackError) {
          console.warn('⚠️ Fallback también falló, usando null para CLABE:', fallbackError);
          clabeResponse = { clabe: null };
        }
      }
      
      const [info, balance, investmentsData, purchasesData] = await Promise.all([
        getCustomerInfo(customerId),
        getCustomerBalance(customerId),
        getCustomerInvestments(customerId),
        getCustomerPurchases(customerId)
      ]);

      console.log('📊 Datos de inversiones recibidos del backend:', investmentsData);
      console.log('📊 Estructura de primera inversión:', investmentsData.length > 0 ? investmentsData[0] : 'No hay inversiones');
      
      // Log detallado de cada inversión
      investmentsData.forEach((inv, index) => {
        console.log(`📈 Inversión ${index + 1}:`, {
          id: inv.id,
          term: inv.term,
          interestRate: inv.interestRate,
          maturityDate: inv.maturityDate,
          returns: inv.returns,
          amount: inv.amount,
          status: inv.status,
          todasLasPropiedades: Object.keys(inv)
        });
      });

      setCustomerInfo(info);
      setBalanceInfo(balance);
      setInvestments(investmentsData);
      setPurchases(purchasesData);
      
      // Usar la CLABE obtenida del endpoint GET /admin/users/clabe
      const clabeValue = clabeResponse?.clabe ?? null;
      console.log('🔍 CLABE final a mostrar:', {
        clabeFromEndpoint: clabeValue,
        clabeFromInfo: info.clabe,
        phoneUsed: phone,
        customerId: customerId,
        willShow: clabeValue ?? info.clabe ?? 'No registrada'
      });
      
      // Establecer la CLABE obtenida del endpoint (prioridad sobre customerInfo.clabe)
      setUserClabe(clabeValue);
      
      // Log para verificar que se estableció correctamente
      console.log('✅ Estado userClabe establecido a:', clabeValue);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar la información del cliente';
      setError(errorMessage);
      console.error('Error loading customer data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerInfo, getCustomerBalance, getCustomerInvestments, getCustomerPurchases, getUserClabe]);

  const handleSearchCustomer = async () => {
    if (!searchPhone.trim()) {
      setError('Por favor ingresa un número de teléfono para buscar');
      return;
    }

    // Normalizar teléfono: remover caracteres no numéricos
    const normalizedPhone = searchPhone.trim().replace(/\D/g, '');
    
    // Validar que tenga 10 dígitos (después de normalizar)
    if (normalizedPhone.length !== 10) {
      setError('El teléfono debe tener exactamente 10 dígitos');
      return;
    }

    setIsSearching(true);
    setError(null);
    setCustomerFound(false);
    
    try {
      const customer = await getCustomerByPhone(normalizedPhone);
      setCustomerId(customer.id);
        setCustomerFound(true);
      // Pasar el teléfono para usar query parameter en lugar de userId
      await loadCustomerData(customer.id, normalizedPhone);
    } catch (err) {
        setCustomerFound(false);
      setCustomerId(null);
      const errorMessage = err instanceof Error ? err.message : 'No se encontró ningún cliente con ese número de teléfono';
      setError(errorMessage);
      console.error('Error searching customer:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleFilterInvestments = async () => {
    if (!customerId) return;

    try {
      setIsLoading(true);
      setError(null);
      const investmentsData = await getCustomerInvestments(
        customerId,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      setInvestments(investmentsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al filtrar inversiones';
      setError(errorMessage);
      console.error('Error filtering investments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-MX');
  };

  const tabs = [
    {
      id: 'general',
      label: 'Información General',
      icon: User,
      description: 'Datos del cliente y balance general'
    },
    {
      id: 'inversiones',
      label: 'Inversiones',
      icon: TrendingUp,
      description: 'Inversiones activas y completadas'
    },
    {
      id: 'saldo',
      label: 'Saldo Disponible',
      icon: Wallet,
      description: 'Saldo y rendimientos disponibles'
    }
  ];

  const renderGeneralSection = () => {
    if (!balanceInfo) return null;

    // Calcular total invertido y rendimientos estimados de inversiones activas
    const activeInvestments = investments.filter(inv => inv.status === 'active');
    
    console.log('📊 Inversiones activas para cálculo:', activeInvestments.length);
    activeInvestments.forEach((inv, idx) => {
      console.log(`  Inversión ${idx + 1}:`, {
        id: inv.id,
        amount: inv.amount,
        interestRate: inv.interestRate,
        interest_rate: (inv as any).interest_rate,
        maturityDate: inv.maturityDate,
        maturity_date: (inv as any).maturity_date,
        todasLasPropiedades: Object.keys(inv)
      });
    });
    
    // Calcular total de dinero invertido en inversiones activas
    const totalInvested = activeInvestments.reduce((sum, inv) => {
      return sum + (inv.amount || 0);
    }, 0);
    console.log('💰 Total invertido activo:', totalInvested);

    // Calcular rendimientos estimados totales
    let totalEstimatedReturns = 0;
    activeInvestments.forEach((investment) => {
      const interestRate = investment.interestRate !== undefined && investment.interestRate !== null 
        ? investment.interestRate 
        : (investment as any).interest_rate !== undefined && (investment as any).interest_rate !== null
        ? (investment as any).interest_rate
        : (investment as any).tasa_interes !== undefined && (investment as any).tasa_interes !== null
        ? (investment as any).tasa_interes
        : null;
      const maturityDate = investment.maturityDate || (investment as any).maturity_date || (investment as any).fecha_vencimiento || null;
      
      console.log('💰 Calculando rendimientos para inversión:', investment.id, {
        interestRate,
        maturityDate,
        amount: investment.amount
      });
      
      if (interestRate !== null && maturityDate) {
        try {
          const maturityDateObj = new Date(maturityDate);
          const now = new Date();
          const daysDiff = Math.max(0, Math.ceil((maturityDateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
          const annualRate = Number(interestRate) / 100;
          const daysInYear = 365;
          const estimatedReturns = investment.amount * annualRate * (daysDiff / daysInYear);
          totalEstimatedReturns += estimatedReturns;
          console.log('  ✅ Rendimientos estimados calculados:', estimatedReturns, 'para', daysDiff, 'días');
        } catch (e) {
          console.error('  ❌ Error calculando rendimientos estimados:', e);
        }
      } else {
        console.log('  ⚠️ Faltan datos para calcular rendimientos (tasa:', interestRate, ', fecha:', maturityDate, ')');
      }
    });
    
    console.log('💰 Total rendimientos estimados:', totalEstimatedReturns);

    return (
    <div className="space-y-6">
      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Balance Total</p>
              <p className="text-xl font-bold text-secondary-900">
                {showBalance ? formatCurrency(balanceInfo.totalBalance) : '••••••'}
              </p>
            </div>
              <DollarSign className="h-8 w-8 text-[#3bbcc8]" />
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Saldo Disponible</p>
              <p className="text-xl font-bold text-green-600">
                {showBalance ? formatCurrency(balanceInfo.availableBalance) : '••••••'}
              </p>
            </div>
            <Wallet className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Inversiones</p>
              <p className="text-xl font-bold text-blue-600">
                {showBalance ? formatCurrency(totalInvested) : '••••••'}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Rendimientos</p>
              <p className="text-xl font-bold text-orange-600">
                {showBalance ? (totalEstimatedReturns > 0 ? formatCurrency(totalEstimatedReturns) : '$0.00') : '••••••'}
              </p>
            </div>
            <PiggyBank className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Show/Hide Balance Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowBalance(!showBalance)}
          className="btn-secondary flex items-center space-x-2"
        >
          {showBalance ? (
            <>
              <EyeOff className="h-4 w-4" />
              <span>Ocultar Saldos</span>
            </>
          ) : (
            <>
              <Eye className="h-4 w-4" />
              <span>Mostrar Saldos</span>
            </>
          )}
        </button>
      </div>

      {/* Customer Information */}
        {customerInfo && (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Información del Cliente</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre Completo</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={`${customerInfo.name} ${customerInfo.lastName}`}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Teléfono</label>
            <input
              type="tel"
              className="input-field bg-secondary-50"
              value={customerInfo.phone}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Correo Electrónico</label>
            <input
              type="email"
              className="input-field bg-secondary-50"
              value={customerInfo.email}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Fecha de Registro</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={formatDate(customerInfo.registrationDate)}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Fecha de Nacimiento</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={formatDate(customerInfo.dateOfBirth)}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Código Postal</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={customerInfo.zipCode}
              disabled
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-secondary-700 mb-2">Dirección</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={customerInfo.address}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Ciudad</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={customerInfo.city}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Estado</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={customerInfo.state}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">CLABE Bancaria</label>
            <input
              type="text"
              className="input-field bg-secondary-50"
              value={userClabe ?? customerInfo?.clabe ?? 'No registrada'}
              disabled
              placeholder="No registrada"
            />
          </div>
        </div>
      </div>
        )}
    </div>
  );
  };

  const renderInversionesSection = () => {
    return (
    <div className="space-y-6">
      {/* Date Filter */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Filtro por Fechas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Fecha Inicio</label>
            <input
              type="date"
              className="input-field"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Fecha Fin</label>
            <input
              type="date"
              className="input-field"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            />
          </div>
          <div className="flex items-end">
              <button 
                onClick={handleFilterInvestments}
                className="btn-primary flex items-center space-x-2 w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Filtrando...</span>
                  </>
                ) : (
                  <>
              <Filter className="h-4 w-4" />
              <span>Filtrar</span>
                  </>
                )}
            </button>
          </div>
        </div>
      </div>

      {/* Investments List */}
      <div className="card p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">Inversiones Activas</h3>
        </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#3bbcc8]" />
              <span className="ml-3 text-secondary-600">Cargando inversiones...</span>
            </div>
          ) : (
            <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-secondary-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Fecha</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Monto</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Plazo</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Tasa de Interés</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Rendimientos Acumulados</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Rendimientos Estimados</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Fecha Vencimiento</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Estado</th>
              </tr>
            </thead>
            <tbody>
                    {investments.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-secondary-500">
                          No hay inversiones registradas
                        </td>
                      </tr>
                    ) : (
                      investments.map((investment) => {
                        // Manejar diferentes formatos de propiedades (snake_case vs camelCase)
                        const term = investment.term || (investment as any).plazo || (investment as any).term_days || null;
                        const interestRate = investment.interestRate !== undefined && investment.interestRate !== null 
                          ? investment.interestRate 
                          : (investment as any).interest_rate !== undefined && (investment as any).interest_rate !== null
                          ? (investment as any).interest_rate
                          : (investment as any).tasa_interes !== undefined && (investment as any).tasa_interes !== null
                          ? (investment as any).tasa_interes
                          : null;
                        const maturityDate = investment.maturityDate || (investment as any).maturity_date || (investment as any).fecha_vencimiento || null;
                        const returns = investment.returns !== undefined && investment.returns !== null
                          ? investment.returns
                          : (investment as any).rendimientos !== undefined && (investment as any).rendimientos !== null
                          ? (investment as any).rendimientos
                          : (investment as any).returns_accumulated !== undefined && (investment as any).returns_accumulated !== null
                          ? (investment as any).returns_accumulated
                          : 0;
                        
                        // Calcular rendimientos estimados si tenemos tasa de interés y fecha de vencimiento
                        let estimatedReturns = null;
                        if (interestRate !== null && maturityDate) {
                          try {
                            const maturityDateObj = new Date(maturityDate);
                            const now = new Date();
                            const daysDiff = Math.max(0, Math.ceil((maturityDateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                            const annualRate = Number(interestRate) / 100;
                            const daysInYear = 365;
                            estimatedReturns = investment.amount * annualRate * (daysDiff / daysInYear);
                          } catch (e) {
                            console.error('Error calculando rendimientos estimados:', e);
                          }
                        }
                        
                        return (
                <tr key={investment.id} className="border-b border-secondary-100">
                  <td className="py-3 px-4 text-sm text-secondary-900">{formatDate(investment.date)}</td>
                  <td className="py-3 px-4 text-sm font-medium text-blue-600">{formatCurrency(investment.amount)}</td>
                            <td className="py-3 px-4 text-sm text-secondary-900">{term || 'N/A'}</td>
                            <td className="py-3 px-4 text-sm text-secondary-900">
                              {interestRate !== null 
                                ? `${Number(interestRate).toFixed(2)}%` 
                                : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm font-medium text-orange-600">{formatCurrency(returns)}</td>
                            <td className="py-3 px-4 text-sm font-medium text-green-600">
                              {estimatedReturns !== null ? formatCurrency(estimatedReturns) : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm text-secondary-900">
                              {maturityDate ? formatDate(maturityDate) : 'N/A'}
                            </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      investment.status === 'active'
                        ? 'bg-blue-100 text-blue-700'
                        : investment.status === 'matured' || investment.status === 'completed'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {investment.status === 'active'
                        ? 'Activa'
                        : investment.status === 'matured' || investment.status === 'completed'
                        ? 'Vencida'
                        : 'Cancelada'}
                    </span>
                  </td>
                </tr>
                        );
                      })
                    )}
            </tbody>
          </table>
        </div>

        {/* Total */}
              {investments.length > 0 && (
        <div className="mt-4 p-4 bg-secondary-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-secondary-700">Total Invertido:</span>
              <span className="text-lg font-bold text-blue-600">
                {formatCurrency(investments.reduce((sum, inv) => sum + inv.amount, 0))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-secondary-700">Total Rendimientos:</span>
              <span className="text-lg font-bold text-orange-600">
                {formatCurrency(investments.reduce((sum, inv) => sum + inv.returns, 0))}
              </span>
            </div>
          </div>
        </div>
              )}
            </>
          )}
      </div>
    </div>
  );
  };

  const renderSaldoSection = () => {
    if (!balanceInfo) return null;

    return (
    <div className="space-y-6">
      {/* Balance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Saldo Disponible</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-secondary-600">Saldo Principal:</span>
              <span className="text-lg font-bold text-green-600">
                {showBalance ? formatCurrency(balanceInfo.availableBalance) : '••••••'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-secondary-600">Rendimientos Disponibles:</span>
              <span className="text-lg font-bold text-orange-600">
                {showBalance ? formatCurrency(balanceInfo.availableReturns) : '••••••'}
              </span>
            </div>
            <div className="border-t border-secondary-200 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-secondary-700">Total Disponible:</span>
                  <span className="text-xl font-bold text-[#3bbcc8]">
                  {showBalance ? formatCurrency(balanceInfo.availableBalance + balanceInfo.availableReturns) : '••••••'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Resumen de Compras</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-secondary-600">Con Saldo Disponible:</span>
              <span className="text-sm font-medium text-green-600">
                {formatCurrency(purchases.filter(p => p.type === 'available_balance').reduce((sum, p) => sum + p.amount, 0))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-secondary-600">Con Rendimientos:</span>
              <span className="text-sm font-medium text-orange-600">
                {formatCurrency(purchases.filter(p => p.type === 'returns').reduce((sum, p) => sum + p.amount, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Purchases List */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Historial de Compras</h3>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#3bbcc8]" />
              <span className="ml-3 text-secondary-600">Cargando compras...</span>
            </div>
          ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-secondary-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Fecha</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Descripción</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Monto</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-secondary-700">Tipo</th>
              </tr>
            </thead>
            <tbody>
                  {purchases.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-secondary-500">
                        No hay compras registradas
                      </td>
                    </tr>
                  ) : (
                    purchases.map((purchase) => (
                <tr key={purchase.id} className="border-b border-secondary-100">
                  <td className="py-3 px-4 text-sm text-secondary-900">{formatDate(purchase.date)}</td>
                  <td className="py-3 px-4 text-sm text-secondary-900">{purchase.description}</td>
                  <td className="py-3 px-4 text-sm font-medium text-red-600">{formatCurrency(purchase.amount)}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      purchase.type === 'available_balance' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {purchase.type === 'available_balance' ? 'Saldo Disponible' : 'Rendimientos'}
                    </span>
                  </td>
                </tr>
                    ))
                  )}
            </tbody>
          </table>
        </div>
          )}
      </div>
    </div>
  );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 bg-[#3bbcc8]/10 rounded-lg flex items-center justify-center">
            <HeadphonesIcon className="h-5 w-5 text-[#3bbcc8]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-secondary-900">Atención a Clientes</h2>
            <p className="text-sm text-secondary-500">Consulta información general, inversiones y saldos</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Buscar Cliente</h3>
            <p className="text-sm text-secondary-500">Ingresa el número de teléfono para consultar información</p>
          </div>
          {customerFound && (
            <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full flex items-center">
              <User className="h-3 w-3 mr-1" />
              Cliente Encontrado
            </span>
          )}
        </div>
        
        <div className="flex space-x-3">
          <div className="flex-1 relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="tel"
              className="input-field pl-10"
              placeholder="Ej: 5512345678"
              value={searchPhone}
              onChange={(e) => {
                // Solo permitir números, máximo 10 dígitos
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setSearchPhone(value);
              }}
              maxLength={10}
              disabled={isSearching}
            />
          </div>
          <button
            onClick={handleSearchCustomer}
            disabled={isSearching || !searchPhone.trim()}
            className="btn-primary flex items-center space-x-2 px-6"
          >
            {isSearching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Buscando...</span>
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                <span>Buscar</span>
              </>
            )}
          </button>
        </div>
        
        {customerFound && customerInfo && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-700">
                Cliente: <strong>{customerInfo.name} {customerInfo.lastName}</strong> - {customerInfo.phone}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="card p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 bg-secondary-100 p-1 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 px-3 lg:px-4 py-2 lg:py-3 rounded-md transition-colors duration-200 ${
                  isActive
                    ? 'bg-white text-[#3bbcc8] shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs lg:text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="card p-6">
        {!customerFound ? (
          <div className="text-center py-12">
            <HeadphonesIcon className="h-16 w-16 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No hay cliente seleccionado</h3>
            <p className="text-secondary-600 mb-4">Busca un cliente por número de teléfono para consultar su información</p>
          </div>
        ) : (
          <>
            {activeTab === 'general' && renderGeneralSection()}
            {activeTab === 'inversiones' && renderInversionesSection()}
            {activeTab === 'saldo' && renderSaldoSection()}
          </>
        )}
      </div>
    </div>
  );
};

export default AtencionSection;
